import { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { getWidget } from '../../checkout/widgets/index.js';
import { formatMoney } from '../../lib/format.js';

/**
 * Renders an EMBEDDED-flow payment: the provider's own widget, on our page.
 *
 * This component knows nothing about any specific provider. It asks the registry
 * for a widget by the `library` name the adapter declared, and each widget
 * exposes one of two shapes:
 *
 *   · `open()`   — a modal the customer launches from our button (Razorpay)
 *   · `mount()`  — something drawn into the page: PayPal's buttons, or the
 *                  CCAvenue iframe that replaces our button entirely
 *
 * Adding a provider therefore changes the registry and nothing here (G6).
 */
const EASE = [0.2, 0.7, 0.2, 1];

export function EmbeddedCheckout({ session, merchant, payment, onResult, onError }) {
  const containerRef = useRef(null);
  const teardownRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const providerSession = session.providerSession;
  const widget = (() => {
    try {
      return getWidget(providerSession?.library ?? providerSession?.kind);
    } catch (err) {
      return { error: err };
    }
  })();

  const fail = useCallback((err) => {
    setStatus('idle');
    setError(err?.message ?? 'We could not process that payment. Please try again.');
    onError?.(err);
  }, [onError]);

  // Widgets that draw themselves mount on arrival; modal widgets wait for a click.
  useEffect(() => {
    if (widget?.error || !containerRef.current) return undefined;
    if (!widget.mount) return undefined;

    let cancelled = false;
    setStatus('loading');

    Promise.resolve(
      widget.mount({
        session: providerSession,
        container: containerRef.current,
        merchant,
        payment,
        onApproved: (result) => { setStatus('submitting'); onResult?.(result); },
        onCancel: () => setStatus('idle'),
        onError: fail,
      }),
    )
      .then((teardown) => {
        if (cancelled) { teardown?.(); return; }
        teardownRef.current = teardown;
        setStatus('ready');
      })
      .catch((err) => { if (!cancelled) fail(err); });

    return () => {
      cancelled = true;
      teardownRef.current?.();
      teardownRef.current = null;
    };
  }, [providerSession?.library, providerSession?.orderId]);

  const launch = useCallback(async () => {
    if (status === 'submitting') return;
    setError(null);
    setStatus('submitting');

    try {
      const result = await widget.open({
        session: providerSession,
        checkoutSessionId: session.sessionId,
        merchant,
        payment,
        onDismiss: () => setStatus('idle'),
      });
      // A dismissed modal is not a failure — the customer simply closed it.
      if (!result) { setStatus('idle'); return; }
      onResult?.(result);
    } catch (err) {
      fail(err);
    }
  }, [widget, providerSession, merchant, payment, status, onResult, fail]);

  if (widget?.error) {
    return (
      <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <p className="text-sm font-medium text-red-700">{widget.error.message}</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ y: 8 }} animate={{ y: 0 }} transition={{ duration: 0.3, ease: EASE }}>
      {error && (
        <div role="alert" className="mb-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm font-medium leading-relaxed text-red-700">{error}</p>
        </div>
      )}

      {/* Where a self-drawing widget lands. Left in the tree for modal widgets
          too, so a switch between them needs no remount. */}
      <div
        ref={containerRef}
        className={widget.fillsFrame ? 'overflow-hidden rounded-xl border border-line bg-surface' : ''}
      />

      {status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading secure payment…
        </div>
      )}

      {/* A modal widget needs our button; a self-drawing one supplies its own. */}
      {!widget.mount && (
        <motion.button
          type="button"
          onClick={launch}
          disabled={status === 'submitting'}
          aria-busy={status === 'submitting' || undefined}
          whileTap={status === 'submitting' ? {} : { scale: 0.985 }}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-[0.94] disabled:opacity-80"
          style={{ background: merchant.accentColor }}
        >
          {status === 'submitting' ? (
            <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Processing…</>
          ) : (
            <><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Pay {formatMoney(payment.amount, payment.currency)}</>
          )}
        </motion.button>
      )}

      {/* HC-14: said plainly, because it is the reassurance that matters most. */}
      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden="true" />
        Your card details are entered directly with the payment network. They never pass through this site.
      </p>
    </motion.div>
  );
}

export default EmbeddedCheckout;
